import {
  type MaskitoMask,
  type MaskitoOptions,
  maskitoTransform,
  maskitoUpdateElement,
} from '@maskito/core';
import { maskitoEventHandler, maskitoWithPlaceholder } from '@maskito/kit';
import {
  type BorrowerInputVariables,
  type OfferCodeInputVariables,
} from '@sparrow-card/sparrow-card-constants';

export type InputVariablesType = BorrowerInputVariables | OfferCodeInputVariables;
export type InputValuesType<K extends InputVariablesType> = Record<K, string>;
export type InputMasksType<K extends InputVariablesType> = Partial<Record<K, MaskitoOptions>>;

const MASKITO_SUPPORTED_INPUT_TYPES = ['text', 'tel', 'search', 'password', 'url'];

export function maskInputValues<K extends InputVariablesType>(
  inputValues: InputValuesType<K>,
  masks: InputMasksType<K>,
): InputValuesType<K> {
  const result = { ...inputValues };

  (Object.keys(result) as K[]).forEach((key) => {
    const mask = masks[key];

    if (mask) {
      result[key] = maskitoTransform(result[key], mask);
    }
  });

  return result;
}

export default maskInputValues;

export const maskWithPlaceholder = (
  mask: MaskitoMask,
  placeholder: string,
  prefix: string = '',
): Pick<Required<MaskitoOptions>, 'mask' | 'plugins' | 'preprocessors' | 'postprocessors'> => {
  const placeholderDefaultOptions = maskitoWithPlaceholder(placeholder);

  const { preprocessors, postprocessors, plugins, removePlaceholder } = placeholderDefaultOptions;

  return {
    mask,
    preprocessors,
    postprocessors,
    plugins: [
      ...plugins,
      maskitoEventHandler('input', (element) => {
        if (!element) {
          return;
        }

        const { value, selectionStart, selectionEnd } = element;

        if (value === placeholder && selectionStart === 0 && selectionEnd === 0) {
          const cleanValue = removePlaceholder(element.value);

          maskitoUpdateElement(element, cleanValue === prefix ? '' : cleanValue);
        }
      }),
    ],
  };
};

export const isSupportedMaskitoInputType = (inputType: React.HTMLInputTypeAttribute) =>
  MASKITO_SUPPORTED_INPUT_TYPES.includes(inputType);
